import { setItemVisual } from './icons';
import { Inventory, type ItemId, ITEMS } from '../game/inventory';
import { StorageView, type StorageOpts } from './storage';
import { DragController } from './drag';
import { SK, titleCss } from './skin';

// Directional hit indicator: a red arc on the edge of the screen pointing toward where a hit came from
// (attacker/explosion), rotated by the angle between the camera's forward and the source direction so it
// reads correctly regardless of orbit. Fades out over DAMAGE_FX.indicatorLifeSec. A fixed pool of arc divs
// (not one per hit) since only a couple can realistically be visible at once.
const INDICATOR_POOL = 4;
export class HitIndicator {
  private readonly root: HTMLDivElement;
  private readonly arcs: HTMLDivElement[] = [];
  private next = 0;

  constructor() {
    this.root = document.createElement('div');
    this.root.style.cssText = css('position:fixed', 'inset:0', 'z-index:7', 'pointer-events:none');
    document.body.appendChild(this.root);
    for (let i = 0; i < INDICATOR_POOL; i++) {
      const arc = document.createElement('div');
      arc.style.cssText = css(
        'position:absolute', 'left:50%', 'top:50%', 'width:0', 'height:0', 'opacity:0',
      );
      const wedge = document.createElement('div');
      wedge.style.cssText = css(
        'position:absolute', 'left:-70px', 'top:-340px', 'width:140px', 'height:140px',
        'background:radial-gradient(ellipse at center, rgba(220,40,30,0.85) 0%, rgba(220,40,30,0) 72%)',
      );
      arc.appendChild(wedge);
      this.root.appendChild(arc);
      this.arcs.push(arc);
    }
  }

  // angleRad: attacker direction relative to camera-forward (0 = straight ahead, +/-PI = behind), so the
  // wedge lands on the correct edge of the screen no matter which way the player is currently facing/orbiting.
  show(angleRad: number, lifeSec: number): void {
    const arc = this.arcs[this.next];
    this.next = (this.next + 1) % this.arcs.length;
    arc.style.transform = `rotate(${angleRad}rad)`;
    arc.style.opacity = '1';
    const anim = arc.animate(
      [{ opacity: 1 }, { opacity: 1, offset: 0.15 }, { opacity: 0 }],
      { duration: lifeSec * 1000, easing: 'ease-out' },
    );
    anim.onfinish = () => { arc.style.opacity = '0'; };
  }
}

// In-game HUD: an inventory panel (I toggle), the two-pane storage screen (open on interact), and a
// bottom-right "gather feed" that floats each hit's yield up and out. (Resource totals live in the
// inventory + the gather feed now; there is no always-on top resource bar.)
export class Hud {
  private readonly invPanel: HTMLDivElement;
  private readonly storage: StorageView;
  private readonly gatherFeed: HTMLDivElement;
  private invOpen = false;

  // Fired whenever a panel (inventory or storage) opens or closes; main uses it to make the hotbar
  // interactive (so you can drag items onto it) only while a screen is up.
  onPanelToggle: ((open: boolean) => void) | null = null;

  constructor(private readonly inv: Inventory, private readonly drag: DragController) {
    this.invPanel = makePanel('50%');
    document.body.appendChild(this.invPanel);

    this.storage = new StorageView(this.inv, this.drag);
    this.storage.onBackdropClose = () => this.closeContainer(); // click/right-click outside the panel closes it (fires onPanelToggle)

    this.gatherFeed = document.createElement('div');
    this.gatherFeed.style.cssText = css(
      'position:fixed', 'right:18px', 'bottom:64px', 'z-index:9', 'display:flex',
      'flex-direction:column-reverse', 'align-items:flex-end', 'gap:3px', 'pointer-events:none',
      `font:14px/1.2 ${SK.font}`,
    );
    document.body.appendChild(this.gatherFeed);

    this.inv.subscribe(() => this.refresh());
    this.refresh();
  }

  toggleInventory(): void {
    this.invOpen = !this.invOpen;
    this.invPanel.style.display = this.invOpen ? 'block' : 'none';
    if (this.invOpen) this.renderInventory();
    this.onPanelToggle?.(this.anyOpen());
  }

  closeInventory(): void {
    if (!this.invOpen) return;
    this.invOpen = false;
    this.invPanel.style.display = 'none';
    this.onPanelToggle?.(this.anyOpen());
  }

  anyOpen(): boolean {
    return this.invOpen || this.storage.isOpen;
  }

  isInventoryOpen(): boolean {
    return this.invOpen;
  }

  // Open any storage (corpse, Base Claim, chest, ...) against your inventory. The storage screen shows
  // your inventory in its right pane, so the standalone inventory panel is closed (single screen at a time).
  openStorage(opts: StorageOpts): void {
    this.invOpen = false;
    this.invPanel.style.display = 'none';
    this.storage.open(opts);
    this.onPanelToggle?.(true);
  }

  isContainerOpen(): boolean {
    return this.storage.isOpen;
  }

  // Repaint the open storage panes (e.g. after a server-authoritative body-loot take mutated the LEFT pane).
  refreshStorage(): void { this.storage.refresh(); }

  // Drop-target hooks (called by the central drag wiring): move a whole stack across the open storage.
  putToStorage(id: ItemId, index?: number): void { this.storage.putToStorage(id, index); }
  putStackToStorage(id: ItemId, n: number): number { return this.storage.putStack(id, n); }
  takeFromStorage(id: ItemId, index?: number): void { this.storage.takeFromStorage(id, index); }
  moveWithinStorage(from: number, to: number): void { this.storage.moveWithinStorage(from, to); } // drag one storage slot onto another (reorder/swap/merge)
  dropFromStorageToWorld(id: ItemId, index?: number): void { this.storage.dropToWorld(id, index); }
  autoCloseStorageIfFar(px: number, pz: number): void { this.storage.autoCloseIfFar(px, pz); } // walk-away closes a world container

  closeContainer(): void {
    this.storage.close();
    this.onPanelToggle?.(this.anyOpen());
  }

  // Float a "Wood +3 (9)" line up the bottom-right feed; it rises and fades over two seconds.
  gather(id: ItemId, gained: number, total: number): void {
    if (gained <= 0) return;
    const row = document.createElement('div');
    row.style.cssText = css('display:flex', 'align-items:center', 'gap:6px', 'white-space:nowrap',
      'text-shadow:0 1px 3px rgba(0,0,0,0.85)');
    const sw = document.createElement('span');
    sw.style.cssText = css('width:9px', 'height:9px', 'border-radius:2px', `background:${cssHex(ITEMS[id].color)}`);
    setItemVisual(sw, id, cssHex(ITEMS[id].color));
    const txt = document.createElement('span');
    txt.innerHTML = `${ITEMS[id].name} <b style="color:${SK.good}">+${gained}</b> <span style="color:${SK.dim}">(${total})</span>`;
    txt.style.color = SK.text;
    row.appendChild(sw);
    row.appendChild(txt);
    this.gatherFeed.appendChild(row);
    const anim = row.animate(
      [
        { transform: 'translateY(10px)', opacity: 0 },
        { transform: 'translateY(2px)', opacity: 1, offset: 0.12 },
        { transform: 'translateY(-14px)', opacity: 0 },
      ],
      { duration: 2000, easing: 'ease-out' },
    );
    anim.onfinish = () => row.remove();
    anim.oncancel = () => row.remove();
  }

  // Contextual stat readout (eating/drinking): floats a coloured line up the same bottom-right feed.
  vitalGain(label: string, color: number): void {
    if (!label) return;
    const row = document.createElement('div');
    row.style.cssText = css('white-space:nowrap', 'font-weight:700', 'text-shadow:0 1px 3px rgba(0,0,0,0.85)');
    row.style.color = cssHex(color);
    row.textContent = label;
    this.gatherFeed.appendChild(row);
    const anim = row.animate(
      [
        { transform: 'translateY(10px)', opacity: 0 },
        { transform: 'translateY(2px)', opacity: 1, offset: 0.12 },
        { transform: 'translateY(-14px)', opacity: 0 },
      ],
      { duration: 1800, easing: 'ease-out' },
    );
    anim.onfinish = () => row.remove();
    anim.oncancel = () => row.remove();
  }

  private refresh(): void {
    if (this.invOpen) this.renderInventory();
    this.storage.refresh();
  }

  private renderInventory(): void {
    const p = this.invPanel;
    p.replaceChildren();
    p.appendChild(titleRow('Inventory', 'I to close'));
    const entries = this.inv.entries();
    if (!entries.length) {
      p.appendChild(line('Empty. Drag items here from a chest, or onto the hotbar to wield.'));
      return;
    }
    const grid = makeGrid();
    grid.dataset.drop = 'inventory'; // a deployable dragged off the hotbar lands back here
    for (const [id, n] of entries) grid.appendChild(this.itemRow(id, n));
    p.appendChild(grid);
  }

  // An inventory row: draggable (onto the other storage pane or a hotbar slot).
  private itemRow(id: ItemId, n: number): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = css('display:flex', 'align-items:center', 'gap:8px', 'cursor:grab', 'user-select:none', 'padding:2px 0');
    const sw = document.createElement('span');
    sw.style.cssText = css('width:14px', 'height:14px', 'border-radius:3px', `background:${cssHex(ITEMS[id].color)}`, 'pointer-events:none');
    setItemVisual(sw, id, cssHex(ITEMS[id].color));
    row.appendChild(sw);
    row.appendChild(span(ITEMS[id].name, `flex:1;color:${SK.text};pointer-events:none;`));
    row.appendChild(span(String(n), `color:${SK.dim};pointer-events:none;`));
    row.addEventListener('mousedown', (e) => {
      this.drag.beginCandidate({ source: 'inventory', id }, `${ITEMS[id].name} (${n})`, e);
    });
    return row;
  }
}

// --- helpers ---

function css(...lines: string[]): string {
  return lines.join(';');
}

function cssHex(hex: number): string {
  return '#' + hex.toString(16).padStart(6, '0');
}

function makePanel(top: string): HTMLDivElement {
  const p = document.createElement('div');
  p.style.cssText = css(
    'position:fixed', 'left:50%', `top:${top}`, 'transform:translate(-50%,-50%)', 'z-index:12',
    'display:none', 'width:min(300px,90vw)', `background:${SK.glass}`,
    `border:1px solid ${SK.borderSoft}`, `border-radius:${SK.rLg}`, 'padding:16px 18px',
    `backdrop-filter:blur(${SK.blur})`, `-webkit-backdrop-filter:blur(${SK.blur})`,
    'box-shadow:0 18px 50px rgba(0,0,0,0.6)', `font:${SK.fsBody}/1.6 ${SK.font}`,
    `color:${SK.text}`,
  );
  return p;
}

function titleRow(title: string, hint: string): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = css('display:flex', 'justify-content:space-between', 'align-items:baseline', 'margin-bottom:12px');
  row.appendChild(span(title.toUpperCase(), titleCss(15) + ';'));
  row.appendChild(span(hint, `font-size:${SK.fsMicro};color:${SK.faint};`));
  return row;
}

function line(s: string): HTMLElement {
  return span(s, `color:${SK.dim};`);
}

function span(s: string, extra: string): HTMLElement {
  const el = document.createElement('span');
  el.textContent = s;
  el.style.cssText = extra;
  return el;
}

function makeGrid(): HTMLElement {
  const grid = document.createElement('div');
  grid.style.cssText = css('display:flex', 'flex-direction:column', 'gap:6px');
  return grid;
}
